import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import { ClipboardList, DollarSign } from "lucide-react";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const RegistrationPage = () => {
  const [contestants, setContestants] = useState([]);
  const [playdays, setPlaydays] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    contestant_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age_group: "mini",
    playday_id: "",
    event_ids: [],
    team_roping_partner_id: "",
    team_roping_partner: "",
    ribbon_roping_partner_id: "",
    ribbon_roping_partner: "",
    roping_fee: false,
    arena_fee: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setError(null);
    try {
      // Fetch playdays and events (public endpoints)
      const [playdaysRes, eventsRes] = await Promise.all([
        axios.get(`${API}/playdays`),
        axios.get(`${API}/events`)
      ]);
      
      setPlaydays(playdaysRes.data);
      setEvents(eventsRes.data);
      
      if (playdaysRes.data.length > 0) {
        setFormData(prev => ({ ...prev, playday_id: playdaysRes.data[0].id }));
      }
      
      // Try to fetch contestants (requires auth, may fail for new users)
      try {
        const contestantsRes = await axios.get(`${API}/contestants`);
        setContestants(contestantsRes.data);
      } catch (e) {
        // Contestants fetch failed - that's ok for new registrations
        setContestants([]);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      setError("Failed to load play days and events. Please try again.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Sort contestants alphabetically by name
  const sortedContestants = [...contestants].sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleContestantSelect = (contestantId) => {
    if (!contestantId || contestantId === "new") {
      setFormData(prev => ({
        ...prev,
        contestant_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        age_group: "mini"
      }));
      return;
    }
    const contestant = contestants.find(c => c.id === contestantId);
    if (contestant) {
      setFormData(prev => ({
        ...prev,
        contestant_id: contestantId,
        first_name: contestant.first_name,
        last_name: contestant.last_name,
        email: contestant.email || "",
        phone: contestant.phone || "",
        age_group: contestant.age_group
      }));
    }
  };

  const toggleEvent = (eventId) => {
    const newEventIds = formData.event_ids.includes(eventId)
      ? formData.event_ids.filter(id => id !== eventId)
      : [...formData.event_ids, eventId];
    
    // Auto-enable roping fee if any roping event is selected
    const hasRopingEvent = newEventIds.some(eid => {
      const event = events.find(e => e.id === eid);
      return event?.name?.toLowerCase().includes('roping');
    });
    
    setFormData(prev => ({
      ...prev,
      event_ids: newEventIds,
      roping_fee: hasRopingEvent
    }));
  };

  const calculateTotal = () => {
    const eventFees = formData.event_ids.reduce((sum, eid) => {
      const event = events.find(e => e.id === eid);
      return sum + (event?.fee || 3);
    }, 0);
    const ropingFee = formData.roping_fee ? 3 : 0;
    const arenaFee = formData.arena_fee ? 3 : 0;
    return eventFees + ropingFee + arenaFee;
  };

  const handlePartnerSelect = (field, contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    const partnerName = contestant ? `${contestant.first_name} ${contestant.last_name}` : "";
    
    if (field === "team") {
      setFormData(prev => ({
        ...prev,
        team_roping_partner_id: contestantId === "none" ? "" : contestantId,
        team_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        ribbon_roping_partner_id: contestantId === "none" ? "" : contestantId,
        ribbon_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.playday_id) {
      toast.error("Please select a play day");
      return;
    }
    
    if (formData.event_ids.length === 0) {
      toast.error("Please select at least one event");
      return;
    }

    if (!formData.first_name || !formData.last_name) {
      toast.error("Please enter contestant name");
      return;
    }

    setSubmitting(true);
    try {
      // Create contestant if new
      let contestantId = formData.contestant_id;
      if (!contestantId) {
        const contestantRes = await axios.post(`${API}/contestants`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email || null,
          phone: formData.phone || null,
          age_group: formData.age_group,
          is_member: false
        });
        contestantId = contestantRes.data.id;
      }

      // Create entry
      await axios.post(`${API}/entries`, {
        contestant_id: contestantId,
        playday_id: formData.playday_id,
        event_ids: formData.event_ids,
        team_roping_partner_id: formData.team_roping_partner_id || null,
        team_roping_partner: formData.team_roping_partner || null,
        ribbon_roping_partner_id: formData.ribbon_roping_partner_id || null,
        ribbon_roping_partner: formData.ribbon_roping_partner || null,
        roping_fee: formData.roping_fee,
        arena_fee: formData.arena_fee
      });

      toast.success("Registration successful!");
      
      // Reset form
      setFormData({
        contestant_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        age_group: "mini",
        playday_id: playdays[0]?.id || "",
        event_ids: [],
        team_roping_partner_id: "",
        team_roping_partner: "",
        ribbon_roping_partner_id: "",
        ribbon_roping_partner: "",
        roping_fee: false,
        arena_fee: true
      });
      fetchData();
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPlayday = playdays.find(p => p.id === formData.playday_id);
  const availableEvents = selectedPlayday 
    ? events.filter(e => selectedPlayday.event_ids.includes(e.id))
    : events;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="registration-error">
        <Card className="border-status-error/30 bg-status-error/5">
          <CardContent className="p-6 text-center">
            <p className="text-status-error mb-4">{error}</p>
            <Button onClick={fetchData} className="bg-primary text-primary-foreground">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (playdays.length === 0) {
    return (
      <div className="space-y-6" data-testid="no-playdays">
        <div>
          <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" />
            Contestant Registration
          </h1>
        </div>
        <Card className="border-primary/20">
          <CardContent className="p-8 text-center">
            <ClipboardList className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No play days scheduled yet</p>
            <p className="text-text-muted text-sm mt-1">Check back later for upcoming events</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="registration-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary" />
          Contestant Registration
        </h1>
        <p className="text-text-secondary mt-1">Register for a play day event</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="registration-form">
        {/* Contestant Selection */}
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Contestant Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {contestants.length > 0 && (
              <div className="space-y-2">
                <Label>Existing Contestant (Optional)</Label>
                <Select
                  value={formData.contestant_id || "new"}
                  onValueChange={handleContestantSelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select or enter new contestant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">+ New Contestant</SelectItem>
                    {sortedContestants.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} ({c.age_group})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="reg-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="reg-lastname"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="reg-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="reg-phone"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Age Group *</Label>
              <Select
                value={formData.age_group}
                onValueChange={(value) => setFormData({ ...formData, age_group: value })}
              >
                <SelectTrigger data-testid="reg-agegroup">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map((ag) => (
                    <SelectItem key={ag} value={ag} className="capitalize">
                      {ag.charAt(0).toUpperCase() + ag.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Play Day & Events */}
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Play Day & Events</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Play Day *</Label>
              <Select
                value={formData.playday_id}
                onValueChange={(value) => setFormData({ ...formData, playday_id: value, event_ids: [] })}
              >
                <SelectTrigger data-testid="reg-playday">
                  <SelectValue placeholder="Select a play day" />
                </SelectTrigger>
                <SelectContent>
                  {playdays.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.date})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Select Events ($3 each)</Label>
              {availableEvents.length === 0 ? (
                <p className="text-sm text-text-muted p-3 bg-surface-muted rounded">
                  No events available for this play day
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                  {availableEvents.map((event) => (
                    <div key={event.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`event-${event.id}`}
                        checked={formData.event_ids.includes(event.id)}
                        onCheckedChange={() => toggleEvent(event.id)}
                      />
                      <label
                        htmlFor={`event-${event.id}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {event.name}
                        {event.age_group && (
                          <span className="text-xs text-text-muted ml-1">({event.age_group})</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-text-muted">{formData.event_ids.length} events selected</p>
            </div>
          </CardContent>
        </Card>

        {/* Partners & Fees */}
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Partners & Fees</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team Roping Partner (Contestant 2)</Label>
                <Select
                  value={formData.team_roping_partner_id || "none"}
                  onValueChange={(value) => handlePartnerSelect("team", value)}
                >
                  <SelectTrigger data-testid="reg-team-partner">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Partner</SelectItem>
                    {sortedContestants
                      .filter(c => c.id !== formData.contestant_id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name} ({c.age_group})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ribbon Roping Partner (Contestant 2)</Label>
                <Select
                  value={formData.ribbon_roping_partner_id || "none"}
                  onValueChange={(value) => handlePartnerSelect("ribbon", value)}
                >
                  <SelectTrigger data-testid="reg-ribbon-partner">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Partner</SelectItem>
                    {sortedContestants
                      .filter(c => c.id !== formData.contestant_id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name} ({c.age_group})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="roping_fee"
                  checked={formData.roping_fee}
                  onCheckedChange={(checked) => setFormData({ ...formData, roping_fee: checked })}
                />
                <label htmlFor="roping_fee" className="text-sm cursor-pointer">
                  Roping Fee - $3 (Optional)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="arena_fee"
                  checked={formData.arena_fee}
                  onCheckedChange={(checked) => setFormData({ ...formData, arena_fee: checked })}
                  disabled
                />
                <label htmlFor="arena_fee" className="text-sm cursor-pointer">
                  Arena Fee - $3 (Required)
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total & Submit */}
        <Card className="border-accent/30 bg-accent/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-accent" />
                <span className="font-heading font-bold text-text-primary">Total Fees</span>
              </div>
              <span className="font-heading font-bold text-2xl text-accent" data-testid="total-fees">
                ${calculateTotal().toFixed(2)}
              </span>
            </div>
            <Button
              type="submit"
              disabled={submitting || formData.event_ids.length === 0}
              className="w-full bg-accent text-white hover:bg-accent/90 font-bold h-12"
              data-testid="reg-submit"
            >
              {submitting ? "Registering..." : "Complete Registration"}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default RegistrationPage;
